import { LISTABLE_PLAN_FAMILIES, ExternalLink, TEST_IDS, SUB_ACTIVE_STATUSES } from '@localstack/constants';
import { EmulatorToggle, ServiceCoverageLink, StartPlanButton } from '@localstack/ui';
import {
  DocumentType,
  EmulatorType,
  LegalAcceptanceSource,
  OrganizationRestrictionReason,
  Permission,
  Plan,
  PlanFamily,
  Subscription,
} from '@localstack/types';
import { Box, Divider, Link, Skeleton, Stack, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, { ReactElement, useCallback } from 'react';

import { ArrowOutward } from '@mui/icons-material';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  groupMultiIntervalPlans,
  LegalService,
  OrganizationsService,
  SubscriptionService,
  useApiEffect,
  useApiGetter,
  useRoutes,
} from '@localstack/services';

import { AppRoute } from '~/config';

import {
  EMULATOR_QUERY_PARAM,
  parseEmulatorParam,
  SHOW_TABS_QUERY_PARAM,
  SUBSCRIPTION_QUERY_PARAM,
} from '~/util/pricing';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { useSubscribeToFreemiumPlan } from '~/hooks/useSubscribeToFreemiumPlan';

import { isSubscriptionSwitchEligible, useDirectSubscribe } from '~/hooks/useDirectSubscribe';

import { EMULATOR_MAP, EMULATORS, PricingPlan, SubTitleText, TitleBox, TitleText } from './components';
import { isPreviewOnlyEmulator } from './components/planFamilies';
import { NoAccessToPricing } from './NoAccessToPricing';

const pickActiveSubscription = (
  subscriptions: Subscription[],
  requestedSubscriptionId: string | null,
): Subscription | undefined => {
  const activeSubscriptions = subscriptions.filter((sub) => SUB_ACTIVE_STATUSES.includes(sub.status));
  const requestedSubscription = activeSubscriptions.find((sub) => sub.id === requestedSubscriptionId);

  if (requestedSubscription) return requestedSubscription;

  return activeSubscriptions.find(isSubscriptionSwitchEligible) ?? activeSubscriptions[0];
};

const ChoiceContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderRadius: '6px',
  border: '1px solid',
  borderColor: theme.palette.divider,
  width: 'fit-content',
  height: 'fit-content',
  '& > *:not(hr)': {
    width: '300px',
  },
}));

const ContactLink = styled(Link)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  paddingLeft: '4px',
}));

const TrialStrip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  width: '100%',
  marginTop: theme.spacing(1),
  padding: theme.spacing(2, 3),
  borderRadius: '6px',
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

const WarningText = styled(SubTitleText)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '16px',
}));

type PricingVariant = 'pricing' | 'trial-expired';

interface PricingPlansContentProps {
  variant: PricingVariant;
}

/**
 * Renders the plan list and plan-selection logic for a single product, driven by the
 * `emulator` URL query parameter. Layout-agnostic so both the in-app pricing page and
 * the no-subscription lockout page can share the same plan logic.
 */
export const PricingPlansContent = ({ variant }: PricingPlansContentProps): ReactElement => {
  const { goto } = useRoutes();
  const { can } = useAuthProvider();
  const theme = useTheme();
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const selectedEmulator = parseEmulatorParam(searchParams.get(EMULATOR_QUERY_PARAM));

  const isTrialExpiredPage = variant === 'trial-expired';
  const showTabs = searchParams.get(SHOW_TABS_QUERY_PARAM) === 'true' || isTrialExpiredPage;

  const legalAcceptanceSource = isTrialExpiredPage
    ? LegalAcceptanceSource.TRIAL_EXPIRED
    : LegalAcceptanceSource.PRICING_PLANS;

  const {
    memberRemovalModal,
    triggerSubscriptionFlow,
    isCheckingEligibility,
    isCreatingSubscription,
    canSubscribeToFreemiumPlan,
  } = useSubscribeToFreemiumPlan();

  const { recordLegalAcceptance } = useApiEffect(LegalService, ['recordLegalAcceptance']);

  const { subscribeDirectly, isLoading: isSubscribingToTrial } = useDirectSubscribe();

  const { data: subscriptions, isLoading: isSubscriptionsLoading } = useApiGetter(
    SubscriptionService,
    'listSubscriptions',
    [],
    {
      enable: can(Permission.READ_SUBSCRIPTIONS),
      // the lockout page cannot act on a failed subscription read, so it stays quiet
      suppressErrors: isTrialExpiredPage,
    },
  );

  // On the lockout page with no emulator in the URL, default to a lone trial's product.
  // Otherwise leave it undefined so the backend infers the product (toggle uses its own default).
  const trialEmulators = (subscriptions ?? [])
    .filter((sub) => sub.plan.family === PlanFamily.TRIAL_PLANS)
    .map((sub) => sub.plan.emulator);
  const trialedEmulator = isTrialExpiredPage && trialEmulators.length === 1 ? trialEmulators[0] : undefined;
  const activeEmulator = selectedEmulator ?? trialedEmulator;

  const { data: plans, isLoading: isPlansLoading } = useApiGetter(SubscriptionService, 'listPlans', [activeEmulator]);

  const { data: orgRestrictions } = useApiGetter(OrganizationsService, 'listOrganizationRestrictions', [], {
    defaultValue: [],
  });
  const isFreemiumHobbyCommercialMisuse =
    orgRestrictions?.some((r) => r.reason === OrganizationRestrictionReason.FREEMIUM_HOBBY_COMMERCIAL_MISUSE) ?? false;

  const isLoading = isPlansLoading || isSubscriptionsLoading || isCheckingEligibility;

  // The pricing page shows plans for a single product at a time. Fall back to the
  // emulator of the returned plans when the URL doesn't specify one (the backend
  // inferred it), so all subscription reasoning below is scoped to the product the
  // user is actually looking at rather than the whole workspace.
  const viewedEmulator = activeEmulator ?? plans?.[0]?.emulator;
  const viewedEmulatorSubscriptions = (subscriptions ?? []).filter((sub) => sub.plan?.emulator === viewedEmulator);

  // A workspace can have two subscriptions for one emulator, so pick the one that can
  // actually be changed.
  const requestedSubscriptionId = searchParams.get(SUBSCRIPTION_QUERY_PARAM);
  const activeSubscription = pickActiveSubscription(viewedEmulatorSubscriptions, requestedSubscriptionId);

  const hasActiveNonTrialSub = viewedEmulatorSubscriptions.some(
    (sub) => SUB_ACTIVE_STATUSES.includes(sub.status) && sub.plan?.family !== PlanFamily.TRIAL_PLANS,
  );

  // Hide the entire freemium family from a user already on a freemium subscription —
  // they shouldn't see another freemium card to "switch" to (legacy plan.freemium owners
  // would otherwise see Hobby; new plan.freemium-2026-03-23 owners would see their own
  // card again). Freemium has plan_seat_limit=1 so there's nothing to manage anyway.
  const hasActiveFreemiumSub = viewedEmulatorSubscriptions.some(
    (sub) => SUB_ACTIVE_STATUSES.includes(sub.status) && sub.plan?.family === PlanFamily.FREEMIUM_PLANS,
  );

  const sortedPlans = (plans ?? [])
    .filter((plan) => {
      const isFreemium = plan.family === PlanFamily.FREEMIUM_PLANS;
      const isVisible = (plan.public && plan.visible) || isFreemium;
      const freemiumBlocked =
        isFreemium && (hasActiveNonTrialSub || hasActiveFreemiumSub || isFreemiumHobbyCommercialMisuse);

      return isVisible && !freemiumBlocked;
    })
    .sort((lp, rp) => LISTABLE_PLAN_FAMILIES.indexOf(lp.family) - LISTABLE_PLAN_FAMILIES.indexOf(rp.family));

  const groupedPlanVariants = groupMultiIntervalPlans(sortedPlans);

  // Pull the trial out of the plan row and give it its own full-width strip below.
  // Presence is the gate: the backend only returns a trial when the viewed product is still
  // eligible. Eligibility is per product, so an expired AWS trial hides the strip on the AWS
  // view while the Snowflake trial is still offered.
  // A trial or a preview is started rather than bought, so neither reaches the checkout page.
  const plansWithoutCheckout = (family: Optional<PlanFamily>) =>
    family === PlanFamily.TRIAL_PLANS || family === PlanFamily.PREVIEW_PLANS;

  const paidPlanVariants = groupedPlanVariants.filter((v) => v[0]?.family !== PlanFamily.TRIAL_PLANS);

  const isPreviewProduct = !!viewedEmulator && isPreviewOnlyEmulator(viewedEmulator);

  const trialPlan = groupedPlanVariants.find((v) => v[0]?.family === PlanFamily.TRIAL_PLANS)?.[0];
  const trialEntry = trialPlan && EMULATOR_MAP[trialPlan.emulator][PlanFamily.TRIAL_PLANS];
  const showTrialBanner = trialPlan && trialEntry;

  const handlePlanSelection = useCallback(
    async (plan: Plan | undefined, existingSubscription: Subscription | undefined) => {
      if (!plan) return;

      // For enterprise plans we want to redirect to the sales page
      if (plan.family === PlanFamily.ENTERPRISE_PLANS) {
        window.open(ExternalLink.CONTACT_SALES, '_blank');
        return;
      }

      // The trial-expired page is a lockout, so send those users straight back to work. Everywhere
      // else the subscription is what they came for, so land them on billing to see it.
      const postSubscribeRoute = isTrialExpiredPage ? AppRoute.GETTING_STARTED : AppRoute.SETTINGS_BILLING;

      // Selecting the Freemium Hobby plan implicitly accepts the Terms of Service
      // (see the ToS fineprint rendered below the Freemium Hobby plan card by PricingPlan).
      if (plan.family === PlanFamily.FREEMIUM_PLANS) {
        await triggerSubscriptionFlow(async () => {
          await recordLegalAcceptance({
            document_type: DocumentType.TOS,
            source: legalAcceptanceSource,
            context: `accepted ToS by selecting the Freemium Hobby plan on the ${variant} page`,
          });
          goto(postSubscribeRoute);
        });
        return;
      }

      // A trial or preview collects no payment info, so there is nothing to fill in on the
      // checkout page. Subscribe directly and skip the checkout.
      if (plansWithoutCheckout(plan.family)) {
        // Switching `existingSubscription` onto the trial cannot overwrite a paid subscription:
        // the backend stops returning the trial plan once the emulator has an active paid subscription.
        const subscription = await subscribeDirectly({ planId: plan.id, existingSubscription });
        if (!subscription) return;

        await recordLegalAcceptance({
          document_type: DocumentType.MSA,
          source: legalAcceptanceSource,
          context: `Implicitly accepted MSA when ${
            plan.family === PlanFamily.PREVIEW_PLANS ? 'joining a public preview' : 'starting a trial'
          } on the ${variant} page`,
        });
        goto(postSubscribeRoute);
        return;
      }

      // For other plans we need to decide whether to take the user to the update or create sub page
      const shouldUpdateExisting = existingSubscription && isSubscriptionSwitchEligible(existingSubscription);

      if (shouldUpdateExisting) {
        goto(
          AppRoute.SETTINGS_SUBSCRIPTION_UPDATE,
          { subscriptionId: existingSubscription.id },
          `plan=${plan.id}&emulator=${plan.emulator}`,
        );
      } else {
        goto(AppRoute.PRICING_SUBSCRIBE, {}, `plan=${plan.id}&emulator=${plan.emulator}`);
      }
    },
    [
      triggerSubscriptionFlow,
      goto,
      recordLegalAcceptance,
      legalAcceptanceSource,
      variant,
      isTrialExpiredPage,
      subscribeDirectly,
    ],
  );

  if (
    !can(Permission.READ_SUBSCRIPTIONS) ||
    !can(Permission.UPDATE_SUBSCRIPTIONS) ||
    !can(Permission.CREATE_SUBSCRIPTIONS)
  ) {
    return <NoAccessToPricing />;
  }

  return (
    <>
      <TitleBox sx={{ mt: 6 }}>
        <TitleText>Select a plan</TitleText>
        <SubTitleText>
          Find the best fit for your individual or team needs. For assistance, please
          <ContactLink target="__blank" href={ExternalLink.CONTACT_SALES} underline="hover">
            Contact Sales <ArrowOutward fontSize="small" />
          </ContactLink>
        </SubTitleText>
      </TitleBox>
      {memberRemovalModal}
      {showTabs && (
        <Stack alignItems="center" sx={{ mt: 1, mb: 3 }}>
          <EmulatorToggle
            emulators={EMULATORS}
            value={viewedEmulator ?? EmulatorType.AWS}
            onChange={(emulator) => {
              const params = new URLSearchParams(search);
              params.set(EMULATOR_QUERY_PARAM, emulator);

              goto(pathname, {}, params.toString());
            }}
          />
        </Stack>
      )}
      {isLoading && (
        <Stack direction="row" gap={2} justifyContent={'center'}>
          {[...Array(4)].map((_) => (
            // eslint-disable-next-line
            <Skeleton width="250px" height={theme.spacing(42)} variant="rectangular" />
          ))}
        </Stack>
      )}
      {!isLoading && (
        <Stack alignItems={'center'}>
          <Stack width="fit-content">
            <ChoiceContainer>
              {paidPlanVariants.map((planVariants, index) => {
                const firstPlanVariant = planVariants[0];
                if (!firstPlanVariant) return null;

                const isFreemiumHobby = firstPlanVariant.family === PlanFamily.FREEMIUM_PLANS;
                const isLockedFreemiumPlan = isFreemiumHobby && !canSubscribeToFreemiumPlan;

                return (
                  <React.Fragment key={firstPlanVariant.id}>
                    <PricingPlan
                      planVariants={planVariants}
                      activeSubscription={activeSubscription}
                      onSelect={
                        !isLockedFreemiumPlan ? (plan) => handlePlanSelection(plan, activeSubscription) : undefined
                      }
                      defaultButtonColor={isFreemiumHobby}
                      highlightedBackground={
                        isTrialExpiredPage && firstPlanVariant.family === PlanFamily.ULTIMATE_PLANS
                      }
                      isButtonLoading={isFreemiumHobby && isCreatingSubscription}
                    />
                    {index < paidPlanVariants.length - 1 && <Divider orientation="vertical" flexItem />}
                  </React.Fragment>
                );
              })}
            </ChoiceContainer>
            {showTrialBanner && (
              <TrialStrip data-testid={TEST_IDS.PRICING_TRIAL_STRIP}>
                <Stack>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Not ready to commit?
                  </Typography>
                  <SubTitleText>{trialEntry.description}</SubTitleText>
                </Stack>
                <StartPlanButton
                  emulator={trialPlan.emulator}
                  buttonText={trialEntry.buttonText}
                  data-testid={TEST_IDS.PRICING_TRIAL_BUTTON}
                  disabled={isSubscribingToTrial}
                  onClick={() => handlePlanSelection(trialPlan, activeSubscription)}
                />
              </TrialStrip>
            )}
          </Stack>
          {!isPreviewProduct && <WarningText>Continue to Purchase lets you purchase with a credit card.</WarningText>}
          <WarningText>
            <ServiceCoverageLink emulator={viewedEmulator ?? EmulatorType.AWS} />
          </WarningText>
        </Stack>
      )}
    </>
  );
};
